/**
 * Hook for checking user permissions in components
 */

import { useAuthStore } from '../stores/auth-store';

// Re-export permission constants for frontend use
export const PERMISSIONS = {
  // User management
  USERS_VIEW: 'users:view',
  USERS_EDIT: 'users:edit',
  USERS_ROLES: 'users:roles',

  // Seed management
  SEEDS_VIEW: 'seeds:view',
  SEEDS_MANAGE: 'seeds:manage',
  SEEDS_CONTROL: 'seeds:control',

  // Store browser
  STORES_VIEW: 'stores:view',
  STORES_MANAGE: 'stores:manage',

  // Knowledge base
  KB_VIEW: 'kb:view',
  KB_MANAGE: 'kb:manage',
  KB_CANDIDATES: 'kb:candidates',

  // FAQs
  FAQS_VIEW: 'faqs:view',
  FAQS_MANAGE: 'faqs:manage',

  // Website content
  WEBSITE_VIEW: 'website:view',
  WEBSITE_EDIT: 'website:edit',
  WEBSITE_THEME: 'website:theme',

  // AI settings
  AI_VIEW: 'ai:view',
  AI_MANAGE: 'ai:manage',
  AI_AGENT: 'ai:agent',

  // Translations
  TRANSLATIONS_VIEW: 'translations:view',
  TRANSLATIONS_MANAGE: 'translations:manage',

  // Support
  SUPPORT_VIEW: 'support:view',
  SUPPORT_RESPOND: 'support:respond',
  SUPPORT_MANAGE: 'support:manage',

  // System
  SYSTEM_VIEW: 'system:view',
  SYSTEM_MANAGE: 'system:manage',
  SYSTEM_MAINTENANCE: 'system:maintenance',

  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view',

  // Social Media Control Center (SMCC)
  SMCC_VIEW: 'smcc:view',
  SMCC_MANAGE: 'smcc:manage',
  SMCC_PUBLISH: 'smcc:publish',
  SMCC_ACCOUNTS: 'smcc:accounts',
  SMCC_ANALYTICS: 'smcc:analytics',
  SMCC_INBOX: 'smcc:inbox',
  SMCC_COMPLIANCE: 'smcc:compliance',

  // Reports
  REPORTS_VIEW: 'reports:view',
  REPORTS_EXPORT: 'reports:export',

  // Slot Machine Skins
  SLOT_SKINS_VIEW: 'slot_skins:view',
  SLOT_SKINS_MANAGE: 'slot_skins:manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// Role types
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
  SUPPORT: 'support',
  CONTENT_MANAGER: 'content_manager',
  USER: 'user',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * Hook to check if user has a specific permission
 */
export function usePermission(permission: Permission): boolean {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  return hasPermission(permission);
}

/**
 * Hook to check if user has any of the specified permissions
 */
export function useAnyPermission(permissions: Permission[]): boolean {
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  return hasAnyPermission(permissions);
}

/**
 * Hook to check if user has all of the specified permissions
 */
export function useAllPermissions(permissions: Permission[]): boolean {
  const hasAllPermissions = useAuthStore((state) => state.hasAllPermissions);
  return hasAllPermissions(permissions);
}

/**
 * Hook to get all user permissions
 */
export function usePermissions(): {
  permissions: string[];
  permissionsLoaded: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
} {
  const permissions = useAuthStore((state) => state.permissions);
  const permissionsLoaded = useAuthStore((state) => state.permissionsLoaded);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasAllPermissions = useAuthStore((state) => state.hasAllPermissions);

  return {
    permissions,
    permissionsLoaded,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

/**
 * Hook to get user's role
 */
export function useRole(): Role | undefined {
  const user = useAuthStore((state) => state.user);
  return user?.role as Role | undefined;
}

/**
 * Component that only renders children if user has permission
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactNode {
  const hasPermission = usePermission(permission);
  return hasPermission ? children : fallback;
}

/**
 * Component that only renders children if user has any of the permissions
 */
export function RequireAnyPermission({
  permissions,
  children,
  fallback = null,
}: {
  permissions: Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}): React.ReactNode {
  const hasAny = useAnyPermission(permissions);
  return hasAny ? children : fallback;
}

// ============================================
// SIMPLIFIED AREA-BASED ACCESS CONTROL SYSTEM
// ============================================

// Access levels for each area
export type AccessLevel = 'none' | 'read' | 'edit';

// All admin areas
export const AREAS = [
  'users',
  'seeds',
  'stores',
  'kb',
  'faqs',
  'website',
  'ai',
  'translations',
  'support',
  'system',
  'smcc',
  'reports',
] as const;

export type Area = (typeof AREAS)[number];

// Map of area to access level
export type AreaAccessMap = Partial<Record<Area, AccessLevel>>;

// Area display info for UI
export const AREA_DISPLAY: Record<Area, { label: string; description: string }> = {
  users: { label: 'Users', description: 'User management and profiles' },
  seeds: { label: 'Seeds', description: 'Seed browser automation' },
  stores: { label: 'Stores', description: 'Store data and configurations' },
  kb: { label: 'Knowledge Base', description: 'KB articles and candidates' },
  faqs: { label: 'FAQs', description: 'FAQ management' },
  website: { label: 'Website', description: 'Website content and theme' },
  ai: { label: 'AI', description: 'AI settings and agent' },
  translations: { label: 'Translations', description: 'Translation strings' },
  support: { label: 'Support', description: 'Support tickets' },
  system: { label: 'System', description: 'System settings and maintenance' },
  smcc: { label: 'Social Media', description: 'Social Media Control Center' },
  reports: { label: 'Reports', description: 'Financial and analytics reports' },
};

// Preset area access configurations
export const AREA_ACCESS_PRESETS: Record<string, { label: string; access: AreaAccessMap }> = {
  no_access: {
    label: 'No Access',
    access: {},
  },
  read_only: {
    label: 'Read Only',
    access: {
      users: 'read',
      seeds: 'read',
      stores: 'read',
      kb: 'read',
      faqs: 'read',
      website: 'read',
      ai: 'read',
      translations: 'read',
      support: 'read',
      system: 'read',
      smcc: 'read',
      reports: 'read',
    },
  },
  full_access: {
    label: 'Full Access',
    access: {
      users: 'edit',
      seeds: 'edit',
      stores: 'edit',
      kb: 'edit',
      faqs: 'edit',
      website: 'edit',
      ai: 'edit',
      translations: 'edit',
      support: 'edit',
      system: 'edit',
      smcc: 'edit',
      reports: 'edit',
    },
  },
  support_role: {
    label: 'Support',
    access: {
      users: 'read',
      kb: 'read',
      support: 'edit',
    },
  },
  content_manager: {
    label: 'Content Manager',
    access: {
      kb: 'edit',
      faqs: 'edit',
      website: 'edit',
      translations: 'edit',
      smcc: 'edit',
    },
  },
  moderator: {
    label: 'Moderator',
    access: {
      users: 'read',
      kb: 'edit',
      faqs: 'edit',
      support: 'read',
      smcc: 'read',
    },
  },
};
